import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

// Define the InteractiveObject interface
interface InteractiveObject {
  mesh: THREE.Object3D;
  url: string;
  label: string;
  interactionDistance: number;
  onInteract?: () => void;
}

// Local array to store interactive objects
const interactiveObjects: InteractiveObject[] = [];

// Extend Window interface to include our array
declare global {
  interface Window {
    registerInteractiveObject: (mesh: THREE.Object3D, url: string, label: string) => void;
    checkInteractiveObjects: (playerPosition: THREE.Vector3) => void;
    interactiveObjects: InteractiveObject[];
  }
}

/**
 * Creates a 3D model of a claw machine using Three.js primitives
 * @returns THREE.Group containing the claw machine model
 */
export function createClawMachine(): THREE.Group {
  // Create a group to hold all parts of the claw machine
  const clawMachineGroup = new THREE.Group();
  clawMachineGroup.name = 'clawMachine';

  // Create the main cabinet frame (now with separate pieces instead of one solid cabinet)
  const cabinetWidth = 1.2;
  const cabinetHeight = 2.0;
  const cabinetDepth = 1.0;
  
  // Create frame pieces
  const frameThickness = 0.05;
  const frameMaterial = new THREE.MeshStandardMaterial({ 
    color: 0x3366cc, // Blue color
    roughness: 0.7,
    metalness: 0.3
  });

  // Bottom base
  const baseGeometry = new THREE.BoxGeometry(cabinetWidth, frameThickness, cabinetDepth);
  const base = new THREE.Mesh(baseGeometry, frameMaterial);
  base.position.y = frameThickness/2;
  base.castShadow = true;
  base.receiveShadow = true;
  clawMachineGroup.add(base);

  // Top frame
  const topGeometry = new THREE.BoxGeometry(cabinetWidth, frameThickness, cabinetDepth);
  const top = new THREE.Mesh(topGeometry, frameMaterial);
  top.position.y = cabinetHeight - frameThickness/2;
  top.castShadow = true;
  clawMachineGroup.add(top);

  // Vertical corner pillars
  const pillarGeometry = new THREE.BoxGeometry(frameThickness, cabinetHeight, frameThickness);
  const pillarPositions: [number, number, number][] = [
    [-cabinetWidth/2 + frameThickness/2, cabinetHeight/2, -cabinetDepth/2 + frameThickness/2],
    [cabinetWidth/2 - frameThickness/2, cabinetHeight/2, -cabinetDepth/2 + frameThickness/2],
    [-cabinetWidth/2 + frameThickness/2, cabinetHeight/2, cabinetDepth/2 - frameThickness/2],
    [cabinetWidth/2 - frameThickness/2, cabinetHeight/2, cabinetDepth/2 - frameThickness/2]
  ];

  pillarPositions.forEach(pos => {
    const pillar = new THREE.Mesh(pillarGeometry, frameMaterial);
    pillar.position.set(...pos);
    pillar.castShadow = true;
    clawMachineGroup.add(pillar);
  });

  // Back panel
  const backPanelGeometry = new THREE.BoxGeometry(cabinetWidth - frameThickness*2, cabinetHeight - frameThickness*2, frameThickness);
  const backPanel = new THREE.Mesh(backPanelGeometry, frameMaterial);
  backPanel.position.set(0, cabinetHeight/2, -cabinetDepth/2);
  backPanel.castShadow = true;
  clawMachineGroup.add(backPanel);

  // Create the glass panels (more transparent now)
  const glassGeometry = new THREE.BoxGeometry(cabinetWidth - frameThickness*2, cabinetHeight * 0.6, cabinetDepth - frameThickness*2);
  const glassMaterial = new THREE.MeshPhysicalMaterial({ 
    color: 0xffffff,
    transparent: true,
    opacity: 0.2,
    roughness: 0,
    metalness: 0.1,
    clearcoat: 1.0,
    clearcoatRoughness: 0.1,
    side: THREE.DoubleSide
  });

  // Front glass panel
  const frontGlass = new THREE.Mesh(
    new THREE.PlaneGeometry(cabinetWidth - frameThickness*2, cabinetHeight * 0.6),
    glassMaterial
  );
  frontGlass.position.set(0, cabinetHeight * 0.6, cabinetDepth/2 - frameThickness/2);
  clawMachineGroup.add(frontGlass);

  // Side glass panels
  const sideGlass1 = new THREE.Mesh(
    new THREE.PlaneGeometry(cabinetDepth - frameThickness*2, cabinetHeight * 0.6),
    glassMaterial
  );
  sideGlass1.rotation.y = Math.PI/2;
  sideGlass1.position.set(cabinetWidth/2 - frameThickness/2, cabinetHeight * 0.6, 0);
  clawMachineGroup.add(sideGlass1);

  const sideGlass2 = sideGlass1.clone();
  sideGlass2.position.set(-cabinetWidth/2 + frameThickness/2, cabinetHeight * 0.6, 0);
  clawMachineGroup.add(sideGlass2);

  // Create the prize chute
  const chuteWidth = cabinetWidth * 0.4;
  const chuteHeight = cabinetHeight * 0.15;
  const chuteDepth = cabinetDepth * 0.3;
  
  const chuteGeometry = new THREE.BoxGeometry(chuteWidth, chuteHeight, chuteDepth);
  const chuteMaterial = new THREE.MeshStandardMaterial({ 
    color: 0x222222, // Dark gray
    roughness: 0.9,
    metalness: 0.1
  });
  const chute = new THREE.Mesh(chuteGeometry, chuteMaterial);
  chute.position.set(0, cabinetHeight * 0.2, cabinetDepth * 0.4);
  clawMachineGroup.add(chute);

  // Create the control panel
  const controlPanelWidth = cabinetWidth * 0.6;
  const controlPanelHeight = cabinetHeight * 0.1;
  const controlPanelDepth = cabinetDepth * 0.2;
  
  const controlPanelGeometry = new THREE.BoxGeometry(controlPanelWidth, controlPanelHeight, controlPanelDepth);
  const controlPanelMaterial = new THREE.MeshStandardMaterial({ 
    color: 0xcc3333, // Red
    roughness: 0.8,
    metalness: 0.2
  });
  const controlPanel = new THREE.Mesh(controlPanelGeometry, controlPanelMaterial);
  controlPanel.position.set(0, cabinetHeight * 0.35, cabinetDepth * 0.5);
  clawMachineGroup.add(controlPanel);

  // Create the joystick
  const joystickBaseRadius = 0.05;
  const joystickBaseHeight = 0.02;
  const joystickBaseGeometry = new THREE.CylinderGeometry(joystickBaseRadius, joystickBaseRadius, joystickBaseHeight, 16);
  const joystickBaseMaterial = new THREE.MeshStandardMaterial({ 
    color: 0x222222,
    roughness: 0.7,
    metalness: 0.3
  });
  const joystickBase = new THREE.Mesh(joystickBaseGeometry, joystickBaseMaterial);
  joystickBase.position.set(controlPanelWidth * 0.25, cabinetHeight * 0.35 + controlPanelHeight/2 + joystickBaseHeight/2, cabinetDepth * 0.5);
  clawMachineGroup.add(joystickBase);

  // Create the joystick handle
  const joystickHandleRadius = 0.02;
  const joystickHandleHeight = 0.1;
  const joystickHandleGeometry = new THREE.CylinderGeometry(joystickHandleRadius, joystickHandleRadius, joystickHandleHeight, 8);
  const joystickHandleMaterial = new THREE.MeshStandardMaterial({ 
    color: 0xff0000,
    roughness: 0.7,
    metalness: 0.3
  });
  const joystickHandle = new THREE.Mesh(joystickHandleGeometry, joystickHandleMaterial);
  joystickHandle.position.set(controlPanelWidth * 0.25, cabinetHeight * 0.35 + controlPanelHeight/2 + joystickBaseHeight + joystickHandleHeight/2, cabinetDepth * 0.5);
  clawMachineGroup.add(joystickHandle);

  // Create the button
  const buttonRadius = 0.04;
  const buttonHeight = 0.02;
  const buttonGeometry = new THREE.CylinderGeometry(buttonRadius, buttonRadius, buttonHeight, 16);
  const buttonMaterial = new THREE.MeshStandardMaterial({ 
    color: 0x00ff00,
    roughness: 0.7,
    metalness: 0.3
  });
  const button = new THREE.Mesh(buttonGeometry, buttonMaterial);
  button.position.set(-controlPanelWidth * 0.25, cabinetHeight * 0.35 + controlPanelHeight/2 + buttonHeight/2, cabinetDepth * 0.5);
  button.rotation.x = Math.PI / 2;
  clawMachineGroup.add(button);

  // Create the claw mechanism with rails
  const clawGroup = new THREE.Group();
  clawGroup.position.set(0, cabinetHeight * 0.85, 0);

  // Add X and Y axis rails
  const railMaterial = new THREE.MeshStandardMaterial({
    color: 0x666666,
    metalness: 0.8,
    roughness: 0.2
  });

  // X-axis rail
  const xRailGeometry = new THREE.BoxGeometry(cabinetWidth - frameThickness*4, 0.03, 0.03);
  const xRail = new THREE.Mesh(xRailGeometry, railMaterial);
  clawGroup.add(xRail);

  // Y-axis rail
  const yRailGeometry = new THREE.BoxGeometry(0.03, 0.03, cabinetDepth - frameThickness*4);
  const yRail = new THREE.Mesh(yRailGeometry, railMaterial);
  clawGroup.add(yRail);
  
  // Claw base (more metallic looking)
  const clawBaseMaterial = new THREE.MeshStandardMaterial({ 
    color: 0x888888,
    roughness: 0.3,
    metalness: 0.8
  });
  const clawBase = new THREE.Mesh(
    new THREE.BoxGeometry(0.1, 0.05, 0.1),
    clawBaseMaterial
  );
  clawBase.position.y = -0.1;
  clawGroup.add(clawBase);
  
  // Create three claw prongs with better materials
  const createClawProng = (angle: number) => {
    const prongGroup = new THREE.Group();
    
    // Prong arm with metallic finish
    const prongArmMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x999999,
      roughness: 0.3,
      metalness: 0.8
    });
    const prongArm = new THREE.Mesh(
      new THREE.BoxGeometry(0.02, 0.12, 0.02),
      prongArmMaterial
    );
    prongArm.position.y = -0.06;
    prongGroup.add(prongArm);
    
    // Prong tip with sharp appearance
    const prongTipMaterial = new THREE.MeshStandardMaterial({ 
      color: 0xcccccc,
      roughness: 0.2,
      metalness: 0.9
    });
    const prongTip = new THREE.Mesh(
      new THREE.ConeGeometry(0.015, 0.04, 8),
      prongTipMaterial
    );
    prongTip.position.y = -0.14;
    prongTip.rotation.x = Math.PI;
    prongGroup.add(prongTip);
    
    // Position the prong at the given angle
    prongGroup.position.x = 0.06 * Math.sin(angle);
    prongGroup.position.z = 0.06 * Math.cos(angle);
    prongGroup.rotation.y = angle;
    
    return prongGroup;
  };
  
  // Add three prongs at 120-degree intervals
  for (let i = 0; i < 3; i++) {
    const angle = (i * Math.PI * 2) / 3;
    const prong = createClawProng(angle);
    clawGroup.add(prong);
  }
  
  clawMachineGroup.add(clawGroup);

  // Add more varied and interesting prizes
  const createPrize = (type: string, x: number, y: number, z: number) => {
    let geometry, material;
    
    switch(type) {
      case 'teddy':
        // Teddy bear-like shape
        const bearGroup = new THREE.Group();
        
        // Body
        const bodyMaterial = new THREE.MeshStandardMaterial({
          color: 0x8B4513,
          roughness: 0.9,
          metalness: 0.1
        });
        const body = new THREE.Mesh(
          new THREE.SphereGeometry(0.08, 12, 12),
          bodyMaterial
        );
        bearGroup.add(body);
        
        // Head
        const head = new THREE.Mesh(
          new THREE.SphereGeometry(0.06, 12, 12),
          bodyMaterial
        );
        head.position.y = 0.1;
        bearGroup.add(head);
        
        // Ears
        const ear1 = new THREE.Mesh(
          new THREE.SphereGeometry(0.02, 8, 8),
          bodyMaterial
        );
        ear1.position.set(0.04, 0.14, 0);
        bearGroup.add(ear1);
        
        const ear2 = ear1.clone();
        ear2.position.set(-0.04, 0.14, 0);
        bearGroup.add(ear2);
        
        bearGroup.position.set(x, y, z);
        return bearGroup;
        
      case 'robot':
        // Robot toy
        const robotGroup = new THREE.Group();
        
        // Body
        const robotMaterial = new THREE.MeshStandardMaterial({
          color: 0x4169E1,
          roughness: 0.4,
          metalness: 0.6
        });
        const robotBody = new THREE.Mesh(
          new THREE.BoxGeometry(0.08, 0.12, 0.08),
          robotMaterial
        );
        robotGroup.add(robotBody);
        
        // Head
        const robotHead = new THREE.Mesh(
          new THREE.BoxGeometry(0.06, 0.06, 0.06),
          robotMaterial
        );
        robotHead.position.y = 0.09;
        robotGroup.add(robotHead);
        
        robotGroup.position.set(x, y, z);
        return robotGroup;
        
      default:
        // Colorful ball
        geometry = new THREE.SphereGeometry(0.06, 16, 16);
        material = new THREE.MeshStandardMaterial({ 
          color: Math.random() * 0xffffff,
          roughness: 0.8,
          metalness: 0.2
        });
        const ball = new THREE.Mesh(geometry, material);
        ball.position.set(x, y, z);
        return ball;
    }
  };
  
  // Add varied prizes
  const prizes = [
    createPrize('teddy', -0.3, cabinetHeight * 0.4, -0.2),
    createPrize('robot', 0.2, cabinetHeight * 0.4, 0.1),
    createPrize('ball', 0.1, cabinetHeight * 0.4, -0.3),
    createPrize('teddy', -0.1, cabinetHeight * 0.4, 0.2),
    createPrize('robot', 0.3, cabinetHeight * 0.4, 0.3),
    createPrize('ball', -0.2, cabinetHeight * 0.4, 0)
  ];
  
  prizes.forEach(prize => clawMachineGroup.add(prize));

  // Add decorative lights on top with glow effect
  const createLight = (x: number) => {
    const lightGroup = new THREE.Group();
    
    // Physical light bulb
    const bulbGeometry = new THREE.SphereGeometry(0.03, 8, 8);
    const bulbMaterial = new THREE.MeshStandardMaterial({ 
      color: 0xffff00,
      emissive: 0xffff00,
      emissiveIntensity: 0.5,
      roughness: 0.3,
      metalness: 0.8
    });
    const bulb = new THREE.Mesh(bulbGeometry, bulbMaterial);
    lightGroup.add(bulb);
    
    // Glow effect
    const glowGeometry = new THREE.SphereGeometry(0.04, 8, 8);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: 0xffff00,
      transparent: true,
      opacity: 0.3
    });
    const glow = new THREE.Mesh(glowGeometry, glowMaterial);
    lightGroup.add(glow);
    
    lightGroup.position.set(x, cabinetHeight + 0.05, 0);
    return lightGroup;
  };
  
  // Add lights across the top
  for (let i = -2; i <= 2; i++) {
    const light = createLight(i * 0.2);
    clawMachineGroup.add(light);
  }

  // Add a sign on top with neon effect
  const signGeometry = new THREE.BoxGeometry(cabinetWidth * 0.9, 0.3, 0.05);
  const signMaterial = new THREE.MeshStandardMaterial({ 
    color: 0xff9900,
    emissive: 0xff9900,
    emissiveIntensity: 0.5,
    roughness: 0.7,
    metalness: 0.3
  });
  const sign = new THREE.Mesh(signGeometry, signMaterial);
  sign.position.set(0, cabinetHeight + 0.2, 0);
  clawMachineGroup.add(sign);

  // Add text to the sign
  const textGeometry = new THREE.PlaneGeometry(cabinetWidth * 0.8, 0.2);
  const textMaterial = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.9
  });
  const text = new THREE.Mesh(textGeometry, textMaterial);
  text.position.set(0, cabinetHeight + 0.2, 0.03);
  clawMachineGroup.add(text);

  // Set up interaction properties
  clawMachineGroup.userData = {
    isInteractive: true,
    interactionDistance: 3,
    label: "Press E to Play Pretzel Claw Game!",
    url: "http://pretzel.pieter.com"
  };

  return clawMachineGroup;
}

/**
 * Adds a claw machine to the back of the room
 * @param scene The Three.js scene
 * @param collisionSystem The collision system for the game
 * @returns The claw machine group
 */
export function addClawMachineToRoom(
  cafeGroup: THREE.Group,
  collisionSystem: any
): Promise<THREE.Group> {
  const clawMachineGroup = new THREE.Group();
  clawMachineGroup.name = 'clawMachine';
  
  // Load the GLB model
  return new Promise((resolve, reject) => {
    const loader = new GLTFLoader();
    loader.load(
      '/assets/claw_machine.glb',
      (gltf) => {
        const model = gltf.scene;
        
        // Scale the model appropriately
        model.scale.set(0.8, 0.8, 0.8); // Adjusted scale to fit against the wall
        
        // Add the model to the group
        clawMachineGroup.add(model);
        
        // Position the claw machine at the middle of the back wall
        // Using the same position as the previous primitive claw machine
        clawMachineGroup.position.set(0, 0, -7.5); // Moved closer to the back wall
        // Rotate it to face forward (away from the wall)
        clawMachineGroup.rotation.y = 0; // Facing away from the wall
        
        // Add to the cafe group
        cafeGroup.add(clawMachineGroup);
        
        // Create a dedicated collision and interaction box that matches the visual model
        const interactionBox = new THREE.Mesh(
          new THREE.BoxGeometry(0.8, 0.8, 0.8), // Much larger box to ensure interaction works
          new THREE.MeshBasicMaterial({ 
            visible: false // Make it invisible
          })
        );
        // Position it explicitly to match the claw machine
        interactionBox.position.set(2, 1, -13.5); // Positioned at the same location with proper height
        interactionBox.rotation.y = 0;
        interactionBox.name = 'clawMachineInteraction';
        
        // Add to the cafe group
        cafeGroup.add(interactionBox);
        
        // Add collision detection to the interaction box
        if (collisionSystem) {
          collisionSystem.addCollider(interactionBox);
        }
        
        // Register the interaction box with the global interaction system
        if (window.registerInteractiveObject) {
          window.registerInteractiveObject(
            interactionBox,
            "http://pretzel.pieter.com",
            "Press E to Play Pretzel Claw Game!"
          );
        }
        
        // Make sure materials and meshes are properly visible
        model.traverse((child: THREE.Object3D) => {
          if (child instanceof THREE.Mesh) {
            child.castShadow = true;
            child.receiveShadow = true;
            
            if (child.material) {
              // Ensure materials are visible
              if (Array.isArray(child.material)) {
                child.material.forEach(mat => {
                  mat.transparent = false;
                  mat.opacity = 1;
                  
                  // Set proper color space for textures
                  if (mat.map) {
                    mat.map.colorSpace = THREE.SRGBColorSpace;
                  }
                });
              } else {
                child.material.transparent = false;
                child.material.opacity = 1;
                
                // Set proper color space for textures
                if (child.material.map) {
                  child.material.map.colorSpace = THREE.SRGBColorSpace;
                }
              }
            }
          }
        });
        
        resolve(clawMachineGroup);
      },
      (progress) => {
        const percent = Math.round(progress.loaded / progress.total * 100);
      },
      (error) => {
        console.error('Error loading claw machine model:', error);
        
        // Fallback to primitive claw machine if model fails to load
        console.log('Falling back to primitive claw machine');
        const fallbackMachine = createClawMachine();
        fallbackMachine.position.set(0, 0, -14.5);
        fallbackMachine.rotation.y = 0;
        cafeGroup.add(fallbackMachine);
        
        if (collisionSystem) {
          collisionSystem.addCollider(fallbackMachine);
        }
        
        if (window.registerInteractiveObject) {
          window.registerInteractiveObject(
            fallbackMachine,
            "http://pretzel.pieter.com",
            "Press E to Play Pretzel Claw Game!"
          );
        }
        
        resolve(fallbackMachine);
      }
    );
  });
} 